import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import { useReportService, useInvalidateReportService } from '../../../hooks/queries/useReportService';
import MobileAgeOutputComponent from '../output/MobileAgeOutputComponent';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';

const MOBILE_REGEX = /^[6-9]\d{9}$/;

const MobileAgeInputComponent = () => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [formData, setFormData] = useState({ mobileNumber: '', consent: false });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    // #161: one shared, cached query instead of a per-component fetch on every
    // mount. Concurrent consumers are deduped into a single request and a
    // revisit is served from cache; the balance is invalidated after a report
    // runs (below), which is what keeps a cached credit figure correct.
    const {
        data: fetchedServiceDetails,
        isFetching: fetchingService,
        isError: serviceError,
    } = useReportService(serviceId);
    const invalidateReportService = useInvalidateReportService();
    const serviceDetails = serviceError
        ? { available_credits: 'N/A', credits_required: 'N/A' }
        : (fetchedServiceDetails || null);

    const handleChange = (e) => {
        const value = (e.target.value || '').replace(/\D/g, '').trim();
        setFormData((prev) => ({ ...prev, mobileNumber: value }));
        if (errors.mobileNumber) setErrors((prev) => ({ ...prev, mobileNumber: '' }));
    };

    const handleConsentChange = (e) => {
        setFormData((prev) => ({ ...prev, consent: e.checked }));
        if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
    };

    const validate = () => {
        const next = {};
        if (!formData.mobileNumber) next.mobileNumber = 'Mobile number is required';
        else if (!MOBILE_REGEX.test(formData.mobileNumber)) next.mobileNumber = 'Enter a valid 10-digit Indian mobile number';
        if (!formData.consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const submit = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post('/surepass/utilities/mobile-age', {
                mobile_number: formData.mobileNumber,
                api_provider_service_id: parseInt(serviceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Lookup failed');
            setReportData(res.data);
            toast.current?.show({ severity: 'success', summary: 'Mobile age fetched', detail: 'Report ready below', life: 2000 });
            // Credits just changed. Invalidating updates every mounted consumer at
            // once and is ordered strictly after the deduction.
            invalidateReportService(serviceId);
        } catch (error) {
            console.error('Mobile age fetch failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            // Fallbacks only. The server sends a message tailored to what
            // actually went wrong — including the case where this verification
            // isn't enabled on the account — and that must win over a canned
            // line keyed on the status code. Blaming the mobile number for
            // every 400 sent people off re-checking a number that was fine.
            const map = {
                500: 'Internal server error. Please try again later.',
                422: 'Please check the mobile number and try again.',
                429: 'Too many requests. Please wait a minute before trying again.',
                400: 'Invalid mobile number. Please verify and retry.',
                404: 'No record found for this mobile number.',
            };
            let detail = error.response?.data?.message || map[status] || error.message || 'Lookup failed';
            if (status === 402) {
                const c = error.response?.data || {};
                detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
            }
            // 3.5s is not long enough to read a "contact support" sentence.
            toast.current?.show({ severity: 'error', summary: 'Lookup failed', detail, life: detail.length > 60 ? 8000 : 3500 });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const next = validate();
        if (Object.keys(next).length > 0) {
            setErrors(next);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix the highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Mobile:</span> <span className="text-900 font-medium">{formData.mobileNumber}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm mobile age check',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, fetch',
            rejectLabel: 'Cancel',
            accept: submit,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const handleAnother = () => {
        setReportData(null);
        setFormData({ mobileNumber: '', consent: false });
        setErrors({});
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-phone"
                        title="Mobile Age"
                        subtitle="Check how long a mobile number has been active via Surepass"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    {!reportData ? (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="grid">
                                    <div className="field col-12 md:col-6 lg:col-4">
                                        <label htmlFor="mobile" className="block font-medium text-sm mb-2">
                                            Mobile Number <span className="text-red-500">*</span>
                                        </label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-phone" />
                                            <InputText
                                                id="mobile"
                                                value={formData.mobileNumber}
                                                onChange={handleChange}
                                                placeholder="9876543210"
                                                maxLength={10}
                                                inputMode="tel"
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.mobileNumber })}
                                                aria-describedby="mobile-help"
                                                aria-invalid={!!errors.mobileNumber}
                                            />
                                        </IconField>
                                        {errors.mobileNumber && (
                                            <small id="mobile-help" className="p-error block mt-1" role="alert">
                                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                                {errors.mobileNumber}
                                            </small>
                                        )}
                                    </div>
                                </div>
                                <ConsentCheckbox
                                    checked={formData.consent}
                                    onChange={handleConsentChange}
                                    error={errors.consent}
                                    reportType="mobile-age"
                                />
                                <div className="flex justify-content-end mt-3">
                                    <Button
                                        type="submit"
                                        label="Check Mobile Age"
                                        icon="pi pi-arrow-right"
                                        iconPos="right"
                                        loading={loading}
                                        disabled={loading}
                                        size="small"
                                    />
                                </div>
                            </form>
                        </Card>
                    ) : (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button
                                    label="Check another"
                                    icon="pi pi-refresh"
                                    severity="secondary"
                                    outlined
                                    size="small"
                                    onClick={handleAnother}
                                />
                            </div>
                            <MobileAgeOutputComponent reportData={reportData} />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default MobileAgeInputComponent;
